import { useCallback, useEffect, useRef, useState } from "react";
import {
    BookOpenIcon,
    FileTextIcon,
    Loader2Icon,
    UploadIcon,
    AlertCircleIcon,
} from "lucide-react";
import { Card, CardContent } from "@component/card";
import { Button } from "@component/button";
import { KnowledgePickerDialog } from "@component/training/knowledge-picker-dialog";
import {
    attachCampaignKnowledge,
    detachCampaignKnowledge,
    excludeCampaignKnowledge,
    includeCampaignKnowledge,
    getCampaignKnowledge,
    listAllKbDocuments,
    uploadKbDocuments,
    type KbDocument,
} from "@service/knowledge-base";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

// PDF only (product decision 2026-07-16) — legacy DOCX rows still render.
const ACCEPTED_EXTENSIONS = [".pdf"];

function formatFileSize(bytes: number): string {
    if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
    return `${Math.round(bytes / 1_000)} KB`;
}

/** One document row, shared by the campaign panel and the create-wizard step. */
export function KbDocRow({
    name,
    subtitle,
    status,
    extractionStatus,
    onRemove,
    removeTitle,
}: {
    name: string;
    subtitle: string;
    status?: KbDocument["status"];
    /** Text-extraction outcome, which is INDEPENDENT of `status`. The BE creates
     *  the row with status="completed" before it attempts extraction, so an
     *  image-only/scanned PDF stays "completed" while its extracted_text is
     *  NULL. Documents with no extracted text are filtered out of the
     *  evaluation set entirely (BE `_resolve_scoped_kb_docs`), so without this
     *  the row renders as healthy while contributing nothing to scoring. */
    extractionStatus?: KbDocument["extractionStatus"];
    onRemove?: () => void;
    removeTitle?: string;
}) {
    return (
        <div className="flex items-center gap-3 px-4 py-3">
            <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                <FileTextIcon className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
            </div>
            {status === "processing" && (
                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200 flex-shrink-0">
                    Processing
                </span>
            )}
            {status === "failed" && (
                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-rose-50 text-rose-700 border-rose-200 flex-shrink-0">
                    Failed
                </span>
            )}
            {/* Upload succeeded but no text could be read, so this document is
                invisible to scoring. Distinct copy from "Failed" — the file is
                fine, it just can't inform an evaluation. */}
            {status !== "failed" && extractionStatus === "failed" && (
                <span
                    title="No text could be read from this document, so it won't inform scoring. Try re-uploading it as a text-based PDF."
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200 flex-shrink-0"
                >
                    Not readable
                </span>
            )}
            {onRemove && (
                // Text button, not an icon — every removal affordance in the
                // knowledge lists reads "Remove" so inherited and campaign rows
                // don't present two different-looking actions for the same verb.
                <button
                    onClick={onRemove}
                    title={removeTitle ?? "Remove"}
                    className="text-xs font-medium flex-shrink-0 px-2 py-1 rounded-md text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                >
                    Remove
                </button>
            )}
        </div>
    );
}

/**
 * Campaign detail → Knowledge tab.
 *
 * Model (matches the create-wizard Knowledge step): workspace-wide documents
 * are INCLUDED BY DEFAULT — they render inside the same "included" list as the
 * campaign's own documents, with a per-campaign "Remove" (an exclusion) rather
 * than an opt-in. Campaign-only docs come from the inline dropzone (or the
 * attach-existing picker, for reusing another campaign's docs without
 * re-uploading).
 *
 * Edits are forward-only by design: they change what FUTURE sessions are
 * evaluated with; completed results keep the knowledge manifest they were
 * scored with (see KnowledgeSourcesSection).
 */
export function CampaignKnowledgePanel({
    campaignId,
    canWrite,
}: {
    campaignId: string;
    canWrite: boolean;
}) {
    const [attached, setAttached] = useState<KbDocument[]>([]);
    const [inherited, setInherited] = useState<KbDocument[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    // The campaign-knowledge endpoint is live in prod (KB Phases 1+2 shipped in
    // ourteam-backend#3588, merged to main 2026-07-17). We keep a defensive
    // 404/405 fallback below: if a given environment is ever behind on the BE, we
    // degrade to the read-only workspace list (those docs DO already apply to this
    // campaign) instead of a scary error state, rather than assuming deployment.
    const [backendPending, setBackendPending] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [attaching, setAttaching] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Request-identity guard: refresh() can be re-fired by a campaign switch or a
    // mutation while an earlier fetch is still in flight. Without this, a slower
    // response for the previous campaign could land last and repaint this panel
    // with the wrong campaign's rows — and the row-level Remove/Include actions
    // would then mutate the CURRENT campaignId using those stale rows. Only the
    // newest load is allowed to commit state.
    const loadSeqRef = useRef(0);

    const refresh = useCallback(async () => {
        const seq = ++loadSeqRef.current;
        try {
            const res = await getCampaignKnowledge(campaignId);
            if (seq !== loadSeqRef.current) return;
            setAttached(res.attached);
            setInherited(res.inherited);
            setLoadError(false);
            setBackendPending(false);
        } catch (err) {
            if (seq !== loadSeqRef.current) return;
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 404 || status === 405) {
                // Defensive: the endpoint is live in prod (BE #3588), but if an
                // environment is ever behind, degrade to the truthful subset:
                // workspace docs (which already apply) via the existing list API.
                try {
                    const docs = await listAllKbDocuments({ scope: "workspace" });
                    if (seq !== loadSeqRef.current) return;
                    setInherited(docs);
                    setAttached([]);
                    setBackendPending(true);
                    setLoadError(false);
                } catch {
                    if (seq !== loadSeqRef.current) return;
                    setLoadError(true);
                }
            } else {
                catchErrorLog(
                    `[training] campaign knowledge load failed: ${
                        err instanceof Error ? err.message : String(err)
                    }`,
                );
                setLoadError(true);
            }
        } finally {
            if (seq === loadSeqRef.current) setLoading(false);
        }
    }, [campaignId]);

    useEffect(() => {
        setLoading(true);
        refresh();
    }, [refresh]);

    async function handleAttach(docs: KbDocument[]) {
        setAttaching(true);
        setActionError(null);
        try {
            await attachCampaignKnowledge(campaignId, docs.map((d) => d.id));
            setPickerOpen(false);
            await refresh();
        } catch {
            setActionError("Couldn't attach the selected documents. Please try again.");
        } finally {
            setAttaching(false);
        }
    }

    async function handleDetach(doc: KbDocument) {
        setActionError(null);
        try {
            await detachCampaignKnowledge(campaignId, doc.id);
        } catch {
            setActionError("Couldn't remove the document. Please try again.");
        } finally {
            // Refetch either way — on failure the row reappears, which is honest.
            refresh();
        }
    }

    async function handleToggleExclusion(doc: KbDocument) {
        setActionError(null);
        try {
            if (doc.excluded) await includeCampaignKnowledge(campaignId, doc.id);
            else await excludeCampaignKnowledge(campaignId, doc.id);
        } catch {
            setActionError(
                doc.excluded
                    ? "Couldn't include the document again. Please try again."
                    : "Couldn't remove the workspace document from this campaign. Please try again.",
            );
        } finally {
            refresh();
        }
    }

    async function handleUpload(files: FileList | File[]) {
        const valid = Array.from(files).filter((f) => {
            const ext = `.${f.name.split(".").pop()?.toLowerCase()}`;
            return ACCEPTED_EXTENSIONS.includes(ext);
        });
        if (valid.length === 0) {
            setActionError("Unsupported format. Use PDF.");
            return;
        }
        setUploading(true);
        setActionError(null);
        try {
            const res = await uploadKbDocuments(valid, undefined, {
                type: "campaign",
                id: campaignId,
            });
            const failed = res.results.filter((r) => r.status === "failed");
            if (failed.length > 0) {
                setActionError(
                    `${failed.length} of ${res.results.length} files failed to upload.`,
                );
            }
            await refresh();
        } catch {
            setActionError("Upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    }

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-16">
                    <Loader2Icon className="h-6 w-6 text-gray-300 animate-spin" />
                </CardContent>
            </Card>
        );
    }

    if (loadError) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                    <AlertCircleIcon className="h-8 w-8 text-gray-300" />
                    <p className="text-sm text-gray-500 mt-3">
                        Couldn't load this campaign's knowledge documents.
                    </p>
                    <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => {
                            setLoading(true);
                            refresh();
                        }}
                    >
                        Retry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const includedCount =
        attached.length + inherited.filter((d) => !d.excluded).length;

    return (
        <div className="space-y-6">
            {/* Section 1 — inline upload dropzone (hidden until the campaign-
                knowledge endpoint is live; the included list below still shows
                the workspace docs that already apply) */}
            {canWrite && !backendPending && (
                <Card className="overflow-hidden">
                    <div className="px-4 pt-4 pb-3 border-b border-gray-100 flex items-start justify-between">
                        <div className="min-w-0 flex-1 pr-4">
                            <h3 className="text-sm font-semibold text-gray-900">Upload documents</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Documents uploaded here will only be used when evaluating this
                                campaign's roleplays. Changes apply to future sessions only.
                            </p>
                        </div>
                        {/* "Attach existing" is intentionally hidden. Reusing another
                            campaign's document introduces a second mental model (docs
                            live in a shared library, reusable across campaigns) that
                            nothing else in this flow teaches, and it's the one control
                            that lets a user deliberately break campaign isolation. The
                            picker + attachCampaignKnowledge wiring is kept intact so
                            this is a one-line restore if the need shows up. */}
                    </div>
                    <div className="p-4">
                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                if (e.dataTransfer.files.length > 0) handleUpload(e.dataTransfer.files);
                            }}
                            onClick={() => fileInputRef.current?.click()}
                            className={cn(
                                "flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer",
                                dragging
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-300 hover:border-blue-400 hover:bg-gray-50",
                                uploading && "opacity-60 pointer-events-none",
                            )}
                        >
                            {uploading ? (
                                <Loader2Icon className="w-8 h-8 mb-3 text-blue-400 animate-spin" />
                            ) : (
                                <UploadIcon className="w-8 h-8 mb-3 text-gray-300" />
                            )}
                            <p className="text-sm font-semibold text-gray-700 mb-0.5">
                                {uploading ? "Uploading…" : "Drag & drop files here"}
                            </p>
                            <p className="text-xs text-gray-500">or click to browse · PDF</p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={ACCEPTED_EXTENSIONS.join(",")}
                                className="hidden"
                                onChange={(e) => {
                                    if (e.target.files?.length) handleUpload(e.target.files);
                                    e.target.value = "";
                                }}
                            />
                        </div>
                        {actionError && (
                            <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 mt-3">
                                <AlertCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                {actionError}
                            </div>
                        )}
                    </div>
                </Card>
            )}

            {/* Section 2 — everything this campaign is evaluated with */}
            <Card className="overflow-hidden">
                <div className="px-4 pt-4 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">
                            Documents included in this campaign
                        </h3>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 tabular-nums">
                            {includedCount}
                        </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Workspace-wide documents apply to every campaign by default.
                        Removing one here only affects this campaign.
                    </p>
                </div>
                {attached.length === 0 && inherited.length === 0 ? (
                    <div className="px-6 py-10 text-center">
                        <BookOpenIcon className="w-8 h-8 mx-auto text-gray-300" />
                        <p className="text-sm text-gray-500 mt-3">
                            No documents yet. This campaign is evaluated on the rubric alone.
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {/* Workspace docs pinned first — they're the default set;
                            campaign-only uploads append below them. */}
                        {inherited.map((doc) => (
                            <div key={doc.id} className={cn(doc.excluded && "opacity-50 bg-gray-50")}>
                                <div className="flex items-center gap-3 px-4 py-3">
                                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                                        <BookOpenIcon className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={cn("text-sm font-medium truncate", doc.excluded ? "text-gray-500 line-through" : "text-gray-900")}>
                                            {doc.name}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            {doc.fileType} · {formatFileSize(doc.sizeBytes)} · Workspace-wide
                                            {doc.excluded && " · Removed from this campaign"}
                                        </p>
                                    </div>
                                    {/* Same caveat as KbDocRow: a workspace doc whose text
                                        couldn't be read is inherited by every campaign yet
                                        informs none of them. Suppressed when excluded — its
                                        readability is moot once it's out of the set. */}
                                    {!doc.excluded && doc.extractionStatus === "failed" && (
                                        <span
                                            title="No text could be read from this document, so it won't inform scoring. Try re-uploading it as a text-based PDF."
                                            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200 flex-shrink-0"
                                        >
                                            Not readable
                                        </span>
                                    )}
                                    {canWrite && !backendPending && (
                                        <button
                                            onClick={() => handleToggleExclusion(doc)}
                                            className={cn(
                                                "text-xs font-medium flex-shrink-0 px-2 py-1 rounded-md transition-colors",
                                                doc.excluded
                                                    ? "text-blue-600 hover:bg-blue-50"
                                                    : "text-gray-400 hover:text-rose-600 hover:bg-rose-50",
                                            )}
                                            title={
                                                doc.excluded
                                                    ? "Include this workspace document again"
                                                    : "Remove this workspace document from this campaign only"
                                            }
                                        >
                                            {doc.excluded ? "Include again" : "Remove"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {attached.map((doc) => (
                            <KbDocRow
                                key={doc.id}
                                name={doc.name}
                                subtitle={`${doc.fileType} · ${formatFileSize(doc.sizeBytes)} · This campaign only`}
                                status={doc.status}
                                extractionStatus={doc.extractionStatus}
                                onRemove={canWrite ? () => handleDetach(doc) : undefined}
                                removeTitle="Remove from this campaign (the document stays in your Knowledge Base)"
                            />
                        ))}
                    </div>
                )}
            </Card>

            <KnowledgePickerDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                excludeIds={attached.map((d) => d.id)}
                onAttach={handleAttach}
                attaching={attaching}
            />
        </div>
    );
}
